import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ChatInitialSessionLoadResult,
  ChatItem,
  ChatSelectedSessionLoadResult,
  SelectedSourceState
} from '../../../models/chat-ui.model';
import { GetSessionByIdResponseDto } from '../../../models/chat/chat-session.models';
import { ChatService } from '../api/chat.service';
import { ChatSessionUiService } from './chat-session-ui.service';
import { ChatStorageService } from './chat-storage.service';
import { ConversationMapperService } from '../mapping/conversation-mapper.service';
import { ChatConversationUiMapperService } from '../mapping/chat-conversation-ui-mapper.service';
import { ChatSourceMapperService } from '../mapping/chat-source-mapper.service';

@Injectable({
  providedIn: 'root'
})
export class ChatSessionLoaderService {
  constructor(
    private chatService: ChatService,
    private chatSessionUi: ChatSessionUiService,
    private chatStorage: ChatStorageService,
    private conversationMapper: ConversationMapperService,
    private conversationUiMapper: ChatConversationUiMapperService,
    private sourceMapper: ChatSourceMapperService
  ) {}

  loadInitialSession(entityIdFactory: () => string): Observable<ChatInitialSessionLoadResult> {
    const sessionId = this.chatStorage.getOrCreateSessionId();

    if (sessionId === '0') {
      return this.chatService.getLastSessions().pipe(
        map(resp => {
          const sessions = resp.sessions || [];
          this.chatSessionUi.setSessions(sessions);

          if (sessions.length === 0) {
            this.chatStorage.setSessionId('0');
            this.chatSessionUi.setSessions([]);
          }

          return {
            kind: 'session-list' as const,
            sessions,
            showSessionModal: sessions.length > 0
          };
        }),
        catchError(error => of(this.buildSessionError(error)))
      );
    }

    return this.chatService.getSessionById(sessionId).pipe(
      map(resp => {
        const loaded = this.mapSessionResponse(sessionId, resp, entityIdFactory);
        this.chatStorage.setSessionId(sessionId);
        return {
          kind: 'session-loaded' as const,
          ...loaded
        };
      }),
      catchError(error => of(this.buildSessionError(error)))
    );
  }

  loadSessionById(sessionId: string, entityIdFactory: () => string): Observable<ChatSelectedSessionLoadResult> {
    this.chatSessionUi.setSelectedSessionId(sessionId);
    return this.chatService.getSessionById(sessionId).pipe(
      map(resp => this.mapSessionResponse(sessionId, resp, entityIdFactory))
    );
  }

  private mapSessionResponse(
    sessionId: string,
    resp: GetSessionByIdResponseDto,
    entityIdFactory: () => string
  ): ChatSelectedSessionLoadResult {
    const mapped = this.conversationMapper.mapConversation(resp.session || []);
    const messages = this.conversationUiMapper.mapConversation(mapped, entityIdFactory, false);

    return {
      sessionId,
      messages,
      selectedState: this.findSelectedState(messages)
    };
  }

  private findSelectedState(messages: ChatItem[]): SelectedSourceState {
    return this.sourceMapper.findSelectedSourceInMessages(messages);
  }

  private buildSessionError(error: unknown): ChatInitialSessionLoadResult {
    this.chatStorage.setSessionId('0');
    this.chatSessionUi.setSessions([]);

    return {
      kind: 'session-error',
      error,
      sessions: [],
      showSessionModal: false
    };
  }
}
